import React, { Fragment, useEffect, useState, } from 'react';
import { Button, Input, } from 'reactstrap';
import { isEmpty, sortBy, orderBy, get, pick, omit, uniq, uniqBy, keyBy, } from 'lodash';
import { toast, } from 'react-toastify';
import { useCounter, useToggle, } from 'react-use';
import { Link } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import classnames from 'classnames';
import { parse as parseCsv } from 'papaparse';
import numeral from 'numeral';
import DatePicker from 'react-datepicker';

import firebase from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { batch, } from '../../shared/firebase';
import { fieldDisplayValue, } from '../../shared/util';
import { statuses, adminFields, } from '../../shared/models/inventory';
import { isMobile, } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ShippingFormModal from '../modals/ShippingFormModal';
import ModelFormModal from '../modals/ModelFormModal';
import QuerySelector from '../QuerySelector';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import ProgressButton from '../ProgressButton';
import ExportButton from '../ExportButton';
import ModalButton from '../ModalButton';
import TenantTags from '../TenantTags';

const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const tagsRef = db.collection('tags');
const inventoriesRef = db.collection('inventories');
const locationsRef = db.collection('locations');

export default AdminPage(function AdminShippings (props) {
  const { user } = props;
  const { tenantId: [tenantId] = [], requestIds: requestIdsForFilter = [], productIds: productIdsForFilter = [], locationIds: locationIdsForFilter = [], } = useQueryParams();
  const [expirationDate, setExpirationDate] = useState(null);
  const [quantity, setQuantity] = useState();
  const tenants = useCollectionSubscription(tenantsRef.orderBy('createdAt'));
  const tags = useCollectionSubscription(tagsRef.orderBy('createdAt'));
  const tagsById = keyBy(tags, 'id');
  const tenantOptions = tenants.map(_ => ({ label: `[${_.id}] ${_.name}`, value: _.id }));
  const tenantsById = keyBy(tenants, 'id');
  const inventories = useCollectionSubscription(tenantId && inventoriesRef.where('tenantId', '==', tenantId).where('status', '==', 'shelved'), [tenantId]);
  const locations = useCollectionSubscription(locationsRef.orderBy('sortKey'));
  const locationById = keyBy(locations, 'id');
  const requestIdOptions = orderBy(uniqBy(inventories.map(_ => ({ ..._, requestId: _.receivingRequestId || _.combiningRequestId, })), _ => _.requestId), _ => numeral(_.requestId).value(), 'desc').map(_ => [_.tenantId, _.requestId].join('-')).map(_ => ({ label: _, value: _ }));
  const productIdOptions = orderBy(uniqBy(inventories, _ => _.productId), _ => numeral(_.productId).value(), 'desc').map(_ => [_.tenantId, _.productId].join('-')).map(_ => ({ label: _, value: _ }));
  const locationIdOptions = locations.map(_ => ({ value: _.id, label: _.name }));

  let filteredInventories = inventories;
  if(!isEmpty(requestIdsForFilter)) {
    filteredInventories = filteredInventories.filter(_ => requestIdsForFilter.includes([_.tenantId, _.receivingRequestId || _.combiningRequestId].join('-')));
  }
  if(!isEmpty(productIdsForFilter)) {
    filteredInventories = filteredInventories.filter(_ => productIdsForFilter.includes([_.tenantId, _.productId].join('-')));
  }

  if(!isEmpty(locationIdsForFilter)) {
    filteredInventories = filteredInventories.filter(_ => locationIdsForFilter.includes(_.locationId));
  }

  const products = useDocumentsFetch(uniqBy(inventories.map(_ => tenantsRef.doc(_.tenantId).collection('products').doc(_.productId)), idWithTenant), [inventories]);
  const productsById = keyBy(products.filter(_ => _), _ => idWithTenant(_.ref));
  const inventoryItems = orderBy(filteredInventories.map((inventory) => {
    const { id, tenantId, productId, } = inventory;
    const tenant = tenantsById[tenantId];
    const product = productsById[tenantId + '-' + productId];
    return {
      ...inventory,
      tenant,
      product,
    };
  }), _ => _.createdAt.toDate(), 'desc');
  const isInvalidQuantity = quantity != null && (quantity <= 0 || quantity > inventoryItems.length);
  const rowsForExport = () => {
    return inventoryItems.map((inventoryItem) => {
      const { id, ref, status, tenantId, tenant, productId, receivingRequestId, locationId, createdAt, isNg = false, expirationDate, } = inventoryItem;
      const { label: statusLabel, color } = statuses[status];
      return {
        id,
        status: statusLabel,
        isNg,
        tenantId: tenant?.id,
        tenantName: tenant?.name,
        receivingRequestId: [tenantId, receivingRequestId].join('-'),
        productId: [tenantId, productId].join('-'),
        locationName: get(locationById, [locationId, 'name']),
        expirationDate: expirationDate && formatDate(expirationDate.toDate(), 'yyyy/MM/dd'),
        createdAt: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm'),
      };
    });
  };
  const onClickApplyExpiration = async () => {
    if(!window.confirm(`現在の絞り込み条件に合致するものの期限を設定します。よろしいですか？`)) return;

    await batch(db, inventoryItems, (batch, inventory) => {
      const { id, ref, isNg, tenantId, receivingRequestId, productId, } = inventory;
      batch.update(tenantsRef.doc(tenantId).collection('products').doc(productId), {
        hasExpiration: true,
      });
      batch.update(ref, { expirationDate, });
    }, 150);
    toast.success('処理しました');
  };
  const reallocateFields = () => ({
    tenantId: {
      type: 'select',
      label: 'テナント',
      options: tenantOptions,
      validations: { required: _ => _ != null, },
    },
  });
  const onSubmitReallocate = async ({ tenantId }, { onClickClose }) => {
    await tenantsRef.doc(tenantId).update({ shouldUpdateStatusOfShippingRequests: true });
    const next = new Date(Math.ceil(new Date().getTime() / (5 * 60 * 1000)) * 5 * 60 * 1000);
    toast.success(`${formatDate(next, 'HH:mm')}に再引当計算されます`);
    onClickClose();
  };
  const onClickChangeStatusAllDisplayItems = async (status) => {
    if(!window.confirm(`現在の絞り込み条件に合致するもの${quantity > 0 ? `のうち${quantity}件` : ''}をまとめて${({ picked: '出庫済み', shipped: '出荷済み' })[status]}にします。よろしいですか？`)) return;

    await batch(db, quantity > 0 ? sortBy(inventoryItems, _ => _.createdAt.toDate()).slice(0, quantity) : inventoryItems, (batch, inventory) => {
      const { id, ref, isNg, tenantId, receivingRequestId, productId, } = inventory;
      batch.update(tenantsRef.doc(tenantId).collection('products').doc(productId), {
        [isNg ? 'ngInventoryIds' : 'goodInventoryIds']: firebase.firestore.FieldValue.arrayRemove(id),
      });
      batch.update(ref, { status, pickedAt: new Date(), });
    }, 150); // NOTE: FieldValueを使うと2つ分消費することがあるので
    toast.success('処理しました');
  };

  return (
    <div className="admin-inventories container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>出荷処理</h4>
        </div>
        <div className="d-flex justify-content-start align-items-end mb-3">
          <AddButton label="出荷" FormModal={ShippingFormModal} formProps={{ user, locations, }} />
          <AddButton className="ml-2" label="FBA出荷" FormModal={ShippingFormModal} formProps={{ user, locations, type: 'amazon', }} />
          <ModalButton className="ml-2" color="warning" Modal={ModelFormModal} modalProps={{ title: '再引当', fields: reallocateFields, onSubmit: onSubmitReallocate, submitLabel: '再引当', }}>
            再引当
          </ModalButton>
          {
            // DEV
            // <AddButton opensInitially className="ml-2" label="FBA出荷" FormModal={ShippingFormModal} formProps={{ user, locations, type: 'amazon', }} />
          }
        </div>
        <hr className="my-5" />
        <div className="mb-3 d-flex flex-column gap-1">
          <div className="d-flex flex-wrap justify-content-start align-items-end gap-1">
            <QuerySelector paramName="tenantId" options={tenantOptions} label="テナント選択" />
            {tenantId && <QuerySelector isMulti paramName="requestIds" options={requestIdOptions} label="入荷依頼IDで絞り込み" />}
            {tenantId && <QuerySelector isMulti paramName="productIds" options={productIdOptions} label="商品IDで絞り込み" />}
            {tenantId && <QuerySelector isMulti paramName="locationIds" options={locationIdOptions} label="ロケーションで絞り込み" />}
          </div>
          <div className="d-flex flex-wrap justify-content-end align-items-end gap-1">
            <div className="border rounded p-2 d-flex justify-content-end align-items-end gap-1">
              <DatePicker
                dateFormat="yyyy/MM/dd"
                selected={expirationDate}
                onChange={_ => setExpirationDate(_)}
                isClearable
              />
              <ProgressButton process={onClickApplyExpiration} disabled={expirationDate == null}>
                期限を設定する
              </ProgressButton>
            </div>
            <div className="border rounded p-2 d-flex justify-content-end align-items-end gap-1">
              <Input type="number" className={classnames('text-right', { 'is-invalid': isInvalidQuantity })} step="1" value={quantity} onChange={_ => setQuantity(_.target.value === '' ? null : parseInt(_.target.value, 10))} style={{ width: 'auto' }} />
              <ProgressButton process={onClickChangeStatusAllDisplayItems.bind(null, 'picked')} disabled={isInvalidQuantity}>
                出庫済みにする
              </ProgressButton>
              <ProgressButton process={onClickChangeStatusAllDisplayItems.bind(null, 'shipped')} disabled={isInvalidQuantity}>
                出荷済みにする
              </ProgressButton>
            </div>
          </div>
          <div className="d-flex flex-wrap justify-content-end align-items-end gap-1">
            <ExportButton fileName="在庫.csv" rows={rowsForExport} />
          </div>
        </div>
        {
          !isMobile() && (
            <div className="mt-4 overflow-scroll position-relative" style={{ zIndex: 0 }}>
              {
                inventoryItems.length > 10000 && (
                  <div className="alert alert-warning">{numeral(inventoryItems.length).format()}個のデータがあります(10,000個のみ表示)</div>
                )
              }
              {
                inventoryItems.length > 0 ? (
                  <table className="table sticky-table table-bordered">
                    <thead className="thead-light text-center">
                      <tr className="text-nowrap">
                        <th style={{ minWidth: 150 }}>ID</th>
                        <th style={{ minWidth: 120 }}>状態</th>
                        <th style={{ minWidth: 120 }}>販売不可</th>
                        <th style={{ minWidth: 120 }}>テナントID</th>
                        <th style={{ minWidth: 150 }}>テナント名</th>
                        <th style={{ minWidth: 120 }}>入荷依頼ID</th>
                        <th style={{ minWidth: 120 }}>商品ID</th>
                        <th style={{ minWidth: 150 }}>ロケーション名</th>
                        <th style={{ minWidth: 150 }}>期限</th>
                        <th style={{ minWidth: 150 }}>入庫日時</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        inventoryItems.slice(0, 10000).map((inventoryItem) => {
                          const { id, ref, status, tenantId, tenant, productId, receivingRequestId, locationId, createdAt, isNg = false, expirationDate, } = inventoryItem;
                          const { label: statusLabel, color } = statuses[status];
                          const withEditBatch = (batch, values) => {
                            if(['picked', 'shipped'].includes(values.status)) {
                              batch.update(tenantsRef.doc(tenantId).collection('products').doc(productId), {
                                [isNg ? 'ngInventoryIds' : 'goodInventoryIds']: firebase.firestore.FieldValue.arrayRemove(id),
                              });
                            }
                          };

                          return (
                            <tr key={id} className={classnames(`table-${color}`)}>
                              <td>
                                {id}
                              </td>
                              <td>
                                {statusLabel}
                              </td>
                              <td>
                                {isNg && '販売不可'}
                              </td>
                              <td>
                                {tenant && tenant.id}
                              </td>
                              <td>
                                {tenant && tenant.name}
                                <TenantTags tagIds={tenant?.tagIds} tagsById={tagsById} />
                              </td>
                              <td>
                                {[tenantId, receivingRequestId].join('-')}
                              </td>
                              <td>
                                {[tenantId, productId].join('-')}
                              </td>
                              <td>
                                {get(locationById, [locationId, 'name'])}
                              </td>
                              <td>
                                {expirationDate && formatDate(expirationDate.toDate(), 'yyyy/MM/dd')}
                              </td>
                              <td>
                                {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}
                              </td>
                              <td className="text-nowrap text-right">
                                <EditButton itemRef={ref} processValues={_ => ({ ..._, ...(['picked', 'shipped'].includes(_.status) && inventoryItem.pickedAt == null && { pickedAt: new Date(), }), })} FormModal={ModelFormModal} formProps={{ title: '在庫 編集', fields: adminFields, }} withBatch={withEditBatch} />
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                ) : (
                  <div>
                    未出庫の在庫はありません
                  </div>
                )
              }
            </div>
          )
        }
      </div>
    </div>
  );
});
